import { router } from "@inertiajs/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { FaChartPie } from "react-icons/fa";
import { TbWallet } from "react-icons/tb";

import ButtonIcon from "@/components/Buttons/SimpleButton";
import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Footer from "@/components/Footer";
import Table from "@/components/Table";
import Typography from "@/components/Typography";
import { dateFormat } from "@/utils/dateFormater";
import { getYearOptions } from "@/utils/getYearOptions";
import {
  moneyCentsFormatterWithoutCurrency,
  moneyFormatterWithoutCurrency,
} from "@/utils/moneyFormat";

import {
  BreakDiv,
  ButtonDiv,
  CenterDiv,
  ColumnDiv,
  DatesSecondDiv,
  FiltersContainer,
  FirstDiv,
  GridInformationBox,
  GridItem,
  IconDiv,
  Line,
  Observation,
  Page,
  SecondDiv,
  StartDiv,
  ThirdDiv,
  Title,
  ValuesDiv,
} from "./styles";

const yearsOptions: string[] = getYearOptions();

const monthOptions: string[] = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const ConsortiumInvestments = ({ info, investors }: any) => {
  const columnHelper = createColumnHelper();
  const today = new Date();
  const [filters, setFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(today.getFullYear()),
    month: new URLSearchParams(window.location.search).get("month")
      ? monthOptions[Number(new URLSearchParams(window.location.search).get("month")) - 1]
      : monthOptions[today.getMonth()],
  });

  const changeFilters = ({ year = filters.year, month = filters.month }) => {
    setFilters({ year, month });
    router.reload({
      only: ["info", "investors"],
      // Needs to sum + 1 because in backend it goes from 0 to 11
      data: { year: year, month: monthOptions.indexOf(month) + 1 },
    });
  };

  // Scroll to investors section if query parameter is present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldScrollToInvestors = urlParams.get("scroll") === "investors";
    if (shouldScrollToInvestors) {
      setTimeout(() => {
        const investorsSection = document.getElementById("investors-section");
        if (investorsSection) {
          investorsSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 1500);
    }
  }, []);

  const investor_columns = [
    columnHelper.accessor("id", {
      header: () => "Código",
      cell: (info: any) => info.row.original.id,
    }),
    columnHelper.accessor("name", {
      header: () => "Nome",
      cell: (cellInfo: any) => <div className="text-primary600">{cellInfo.row.original.name}</div>,
    }),
    columnHelper.accessor("email", {
      header: () => "Email",
      cell: (info: any) => info.row.original.email,
    }),
    columnHelper.accessor("shares_quantity", {
      header: () => "Quantidade de Cotas",
      cell: (info: any) => info.row.original.shares_quantity,
    }),
    columnHelper.accessor("invested_value", {
      header: () => "Valor Investido",
      cell: (info: any) =>
        info.row.original.invested_value
          ? `R$${moneyCentsFormatterWithoutCurrency(info.row.original.invested_value)}`
          : "-",
    }),
    columnHelper.accessor("last_dividend", {
      header: () => "Último Dividendo",
      cell: (info: any) => {
        const lastDividend = info.row.original.last_dividend;
        if (lastDividend && lastDividend.value > 0) {
          return (
            <div className="flex flex-col">
              <span>{`R$${moneyCentsFormatterWithoutCurrency(lastDividend.value)}`}</span>
              <span className="text-xs text-gray-500">{lastDividend.date}</span>
            </div>
          );
        }
        return "-";
      },
    }),
    columnHelper.accessor("dividend_yield", {
      header: () => "Dividend Yield",
      cell: (info: any) => {
        const yieldValue = info.row.original.dividend_yield;
        return yieldValue !== undefined && yieldValue !== null ? `${yieldValue}%` : "-";
      },
    }),
  ];

  const date = info.created_at.split("T");
  return (
    <>
      <FirstDiv>
        <ButtonDiv>
          <ButtonIcon
            onClick={() => router.get("/manager/spes")}
            variant="simple"
            label="Voltar para SPEs"
            className="max-w-60"
          />
          <ButtonIcon
            onClick={() => router.get(`/manager/spes/${info.id}`)}
            variant="simple"
            label="Ver Detalhes"
            className="max-w-60"
          />
        </ButtonDiv>
        <StartDiv>
          <FiltersContainer>
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => {
                changeFilters({ year: value });
              }}
              defaultValue={filters.year}
            />
            <Dropdown
              options={monthOptions}
              onSelect={(value) => {
                changeFilters({ month: value });
              }}
              defaultValue={filters.month}
            />
          </FiltersContainer>
          <div className="mt-4 flex w-full justify-between">
            <ColumnDiv>
              <Typography variant="overline" color="white">
                NOME
              </Typography>
              <Typography variant="h4" color="white">
                {info.name}
              </Typography>
            </ColumnDiv>
            <div className="col">
              <DatesSecondDiv>
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    CLIENTES
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {info.energy_expenses_size}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    CAPACIDADE DE CONSUMO
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {info.installed_capacity} kwh
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    DISTRIBUIDORA
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {info.distributor_name}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    DATA DE ADESÃO
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {dateFormat(date[0])}
                  </Typography>
                </BreakDiv>
              </DatesSecondDiv>
            </div>
          </div>
        </StartDiv>
      </FirstDiv>
      <SecondDiv>
        <Title>
          <Typography variant="h4" color="black">
            Investimentos
          </Typography>
          <Observation>
            <Typography variant="pLarge" color="black">
              Dados sobre cotas, valor atual e dividendos da SPE.
            </Typography>
          </Observation>
        </Title>
        <GridInformationBox>
          <GridItem>
            <Typography variant="pSmall" color="black" className="font-semibold">
              Quantidade de Cotas
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <FaChartPie size={16} color="white" />
              </IconDiv>
              <Typography variant="h4" color="white">
                {info.share_quantity || 0}
              </Typography>
            </ValuesDiv>
          </GridItem>
          <GridItem>
            <Typography variant="pSmall" color="black" className="font-semibold">
              Valor Atual da SPE
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <TbWallet size={16} color="white" />
              </IconDiv>
              <Typography variant="h4" color="white">
                R$ {moneyFormatterWithoutCurrency(info.current_value || 0)}
              </Typography>
            </ValuesDiv>
          </GridItem>
          <GridItem>
            <Typography variant="pSmall" color="black" className="font-semibold">
              Valor por Cota
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <TbWallet size={16} color="white" />
              </IconDiv>
              <Typography variant="h4" color="white">
                R${" "}
                {info.share_quantity && info.share_quantity > 0
                  ? moneyFormatterWithoutCurrency((info.current_value || 0) / info.share_quantity)
                  : moneyFormatterWithoutCurrency(0)}
              </Typography>
            </ValuesDiv>
          </GridItem>
        </GridInformationBox>
        <GridInformationBox>
          <GridItem>
            <Typography variant="pSmall" color="black" className="font-semibold">
              Quantidade de Investidores
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <FaChartPie size={16} color="white" />
              </IconDiv>
              <Typography variant="h4" color="white">
                {info.shares_count || 0}
              </Typography>
            </ValuesDiv>
          </GridItem>
          <GridItem>
            <Typography variant="pSmall" color="black" className="font-semibold">
              Dividendos Criados
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <TbWallet size={16} color="white" />
              </IconDiv>
              <Typography variant="h4" color="white">
                {info.dividends_count || 0}
              </Typography>
            </ValuesDiv>
          </GridItem>
          <GridItem>
            <Typography variant="pSmall" color="black" className="font-semibold">
              Total Pago
            </Typography>
            <ValuesDiv>
              <IconDiv>
                <TbWallet size={16} color="white" />
              </IconDiv>
              <Typography variant="h4" color="white">
                R$ {moneyFormatterWithoutCurrency(info.total_dividends_paid || 0)}
              </Typography>
            </ValuesDiv>
          </GridItem>
        </GridInformationBox>
      </SecondDiv>

      {investors && investors.length > 0 && (
        <ThirdDiv id="investors-section">
          <Typography variant="h4" color="black">
            Investidores
          </Typography>
          <FiltersContainer>
            <div className="w-1/10" />
          </FiltersContainer>
          <CenterDiv className="flex-col space-y-2 rounded-2xl">
            <Table
              data={investors}
              columns={investor_columns}
              onClickRow={(investorId: string) =>
                router.get(`/investor/${investorId}?from=consortium&consortium_id=${info.id}`)
              }
              allowMainRowClick={true}
            />
          </CenterDiv>
        </ThirdDiv>
      )}

      <Page>
        <FaqCardComponent variant="questionSection" />
      </Page>
      <Footer />
    </>
  );
};

export default ConsortiumInvestments;
